import { getPlural } from "../string/getPlural";
import {
  DAYS_PER_MONTH,
  HOURS_PER_DAY,
  MONTHS_PER_YEAR,
  ROUNDING_THRESHOLDS,
} from "./time.constants";
import type { TimeUnits } from "./time.types";

/**
 * Get rounded single unit display - shows only the most significant time unit with rounding
 * @param units - Time units object to convert to a single rounded unit
 * @returns human-readable duration string with single unit
 */
export function getRoundedSingleUnit(units: TimeUnits): string {
  const { years, months, days, hours, minutes: mins } = units;

  // years
  if (years > 0 || months > MONTHS_PER_YEAR) {
    // carry a >=15-day remainder into months first, same as the months branch
    // below, so e.g. "1 year 6 months 25 days" rounds to 2 years, not 1
    const effectiveMonths =
      months + (days > ROUNDING_THRESHOLDS.DAYS_TO_MONTH ? 1 : 0);
    const totalYears = years + (effectiveMonths > 6 ? 1 : 0);
    return `${totalYears} ${getPlural(totalYears, "year")}`;
  }

  // months
  if (months > 0 || days > DAYS_PER_MONTH) {
    const totalMonths = months + (days > ROUNDING_THRESHOLDS.DAYS_TO_MONTH ? 1 : 0);
    return `${totalMonths} ${getPlural(totalMonths, "month")}`;
  }

  // days
  if (days > 0 || hours > HOURS_PER_DAY) {
    const totalDays = days + (hours > ROUNDING_THRESHOLDS.HOURS_TO_DAY ? 1 : 0);
    return `${totalDays} ${getPlural(totalDays, "day")}`;
  }

  // hours
  if (hours > 0 || mins > ROUNDING_THRESHOLDS.MINUTES_TO_HOUR) {
    const totalHours = hours + (mins > ROUNDING_THRESHOLDS.MINUTES_TO_HOUR ? 1 : 0);
    return `${totalHours} ${getPlural(totalHours, "hour")}`;
  }

  // Minutes - show as-is, minimum 1 hour for very small values
  if (mins > 0) {
    return "1 hour";
  }

  // Fallback for zero duration
  return "0 minutes";
}
